import React, { useEffect, useMemo, useState } from 'react'
import { Card, Empty } from 'antd'

const HEIGHT = 320;
const MARGIN = { top: 16, right: 24, bottom: 36, left: 44 };
const MAX_AXIS_LABELS = 12;

// The SVG is drawn at real pixel width so axis text stays at its intended size
// instead of being scaled by a viewBox.
//
// The measured node is held in state rather than a ref: the chart only mounts once
// data has loaded, and a ref object is stable across renders, so an effect keyed on
// it would run once against a null node and never measure the div that arrives later.
function useContainerWidth() {
    const [node, setNode] = useState(null);
    const [width, setWidth] = useState(0);
    useEffect(() => {
        if (!node) return undefined;
        const update = () => setWidth(node.clientWidth);
        update();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update);
            return () => window.removeEventListener('resize', update);
        }
        const observer = new ResizeObserver(update);
        observer.observe(node);
        return () => observer.disconnect();
    }, [node]);
    return [setNode, width];
}

// Rounds the axis up to a whole-number step, since detection counts are integers
// and a fractional gridline would be meaningless.
function buildTicks(max) {
    const target = Math.max(max, 1);
    const rough = target / 4;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
    const normalised = rough / magnitude;
    const niceStep = (normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10) * magnitude;
    const step = Math.max(1, Math.round(niceStep));
    const top = Math.ceil(target / step) * step;
    const ticks = [];
    for (let value = 0; value <= top; value += step) ticks.push(value);
    return { ticks, top };
}

const DetectionTrends = ({ buckets, series, granularity, max }) => {
    const [containerRef, width] = useContainerWidth();
    const [hoverIndex, setHoverIndex] = useState(null);

    const { ticks, top } = useMemo(() => buildTicks(max), [max]);

    const plotWidth = Math.max(width - MARGIN.left - MARGIN.right, 0);
    const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

    // A single bucket has no span to spread across, so pin it to the middle.
    const xFor = (index) => (buckets.length === 1
        ? MARGIN.left + plotWidth / 2
        : MARGIN.left + (index * plotWidth) / (buckets.length - 1));
    const yFor = (value) => MARGIN.top + plotHeight - (value / top) * plotHeight;

    const labelStep = Math.ceil(buckets.length / MAX_AXIS_LABELS);

    const onMove = (event) => {
        if (!buckets.length || plotWidth <= 0) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - bounds.left - MARGIN.left;
        const ratio = plotWidth === 0 ? 0 : x / plotWidth;
        const index = Math.round(ratio * (buckets.length - 1));
        setHoverIndex(Math.min(Math.max(index, 0), buckets.length - 1));
    };

    const hovered = hoverIndex === null ? null : buckets[hoverIndex];
    // Flip the tooltip to the left of the guide line once it would overflow the card.
    const tooltipFlipped = hovered ? xFor(hoverIndex) > MARGIN.left + plotWidth * 0.6 : false;

    return (
        <Card bordered={false} className='analytics-chart-card'>
            <div className='analytics-chart-head'>
                <div className='analytics-chart-title'>Detection Trends</div>
                <div className='analytics-chart-sub'>
                    Detections per {granularity === 'day' ? 'day' : 'month'} by type
                </div>
            </div>

            {!buckets.length ? (
                <Empty description='No detections to plot for these filters.' />
            ) : (
                <>
                    <div className='analytics-chart-legend'>
                        {series.map((type) => (
                            <span key={type.key} className='analytics-chart-legend-item'>
                                <i className='analytics-chart-swatch' style={{ backgroundColor: type.color }} />
                                {type.label}
                            </span>
                        ))}
                    </div>

                    <div className='analytics-chart-body' ref={containerRef}>
                        {width > 0 && (
                            <svg
                                width={width}
                                height={HEIGHT}
                                onMouseMove={onMove}
                                onMouseLeave={() => setHoverIndex(null)}
                                role='img'
                                aria-label={`Detection counts per ${granularity} by detection type`}
                            >
                                {ticks.map((tick) => (
                                    <g key={tick}>
                                        <line
                                            x1={MARGIN.left}
                                            x2={MARGIN.left + plotWidth}
                                            y1={yFor(tick)}
                                            y2={yFor(tick)}
                                            stroke={tick === 0 ? '#d9d9d9' : '#f0f0f0'}
                                        />
                                        <text
                                            x={MARGIN.left - 10}
                                            y={yFor(tick) + 4}
                                            textAnchor='end'
                                            className='analytics-chart-axis-text'
                                        >
                                            {tick}
                                        </text>
                                    </g>
                                ))}

                                {buckets.map((bucket, index) => (
                                    index % labelStep === 0 && (
                                        <text
                                            key={bucket.key}
                                            x={xFor(index)}
                                            y={HEIGHT - 12}
                                            textAnchor='middle'
                                            className='analytics-chart-axis-text'
                                        >
                                            {bucket.axisLabel}
                                        </text>
                                    )
                                ))}

                                {hovered && (
                                    <line
                                        x1={xFor(hoverIndex)}
                                        x2={xFor(hoverIndex)}
                                        y1={MARGIN.top}
                                        y2={MARGIN.top + plotHeight}
                                        stroke='#bfbfbf'
                                        strokeDasharray='3 3'
                                    />
                                )}

                                {series.map((type) => (
                                    <polyline
                                        key={type.key}
                                        fill='none'
                                        stroke={type.color}
                                        strokeWidth='2'
                                        strokeLinejoin='round'
                                        strokeLinecap='round'
                                        points={buckets
                                            .map((bucket, index) => `${xFor(index)},${yFor(bucket.counts[type.key])}`)
                                            .join(' ')}
                                    />
                                ))}

                                {/* Markers only on non-zero points: with sparse data the lines sit on
                                    the baseline, and dotting every zero would bury the real values. */}
                                {series.map((type) => buckets.map((bucket, index) => (
                                    bucket.counts[type.key] > 0 && (
                                        <circle
                                            key={`${type.key}-${bucket.key}`}
                                            cx={xFor(index)}
                                            cy={yFor(bucket.counts[type.key])}
                                            r={hoverIndex === index ? 4.5 : 3}
                                            fill='#fff'
                                            stroke={type.color}
                                            strokeWidth='2'
                                        />
                                    )
                                )))}
                            </svg>
                        )}

                        {hovered && (
                            <div
                                className='analytics-chart-tooltip'
                                style={{
                                    left: tooltipFlipped ? undefined : xFor(hoverIndex) + 14,
                                    right: tooltipFlipped ? width - xFor(hoverIndex) + 14 : undefined,
                                    top: MARGIN.top,
                                }}
                            >
                                <div className='analytics-chart-tooltip-title'>{hovered.tooltipLabel}</div>
                                {series.map((type) => (
                                    <div key={type.key} className='analytics-chart-tooltip-row'>
                                        <i className='analytics-chart-swatch' style={{ backgroundColor: type.color }} />
                                        <span className='analytics-chart-tooltip-label'>{type.label}</span>
                                        <strong>{hovered.counts[type.key]}</strong>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </Card>
    )
}

export default DetectionTrends
